import {
  Container,
  Title,
  Text,
  Tabs,
  List,
  ThemeIcon,
  Box,
  Stack,
  useComputedColorScheme,
} from "@mantine/core";
import {
  IconCheck,
  IconUserCog,
  IconStethoscope,
  IconBriefcase,
  IconClipboardCheck,
} from "@tabler/icons-react";
import { AnimateIn } from "./AnimateIn";

const useCases = [
  {
    value: "managers",
    label: "For Facility Managers",
    icon: IconUserCog,
    color: "#4a8c71",
    features: [
      "Multi-facility dashboard with real-time metrics",
      "Staff presence tracking — see who's checked in at each facility in real time",
      "Role-based access control that gates sensitive actions to on-duty staff",
      "Incident tracking and resolution workflows",
      "Facility task dashboard — open, overdue, and missed work at a glance",
      "Audit log access and compliance reporting",
      "Employee management and training assignments",
      "Custom forms creation and submission review",
      "Budget-friendly pricing that scales with your needs",
    ],
  },
  {
    value: "medical",
    label: "For Medical Staff",
    icon: IconStethoscope,
    color: "#f06595",
    features: [
      "Complete medication lifecycle management",
      "Care plan creation with goal tracking",
      "Vital signs monitoring and trends",
      "A personal shift worklist — meds, vitals, ADL, and forms, with overdue items front and center",
      "Appointment scheduling and tracking",
      "Physician and pharmacy management",
      "Progress notes and clinical documentation",
      "Emergency contact quick access",
      "Mobile-friendly for on-the-go care",
    ],
  },
  {
    value: "admins",
    label: "For Administrators",
    icon: IconBriefcase,
    color: "#228be6",
    features: [
      "Platform-wide oversight across all facilities",
      "Subscription and billing management",
      "Role-based access control (8 built-in roles)",
      "Comprehensive audit logs and activity tracking",
      "External auditor group management",
      "Training material library and LMS",
      "Custom form templates with drag-and-drop builder",
      "Advanced analytics and reporting",
    ],
  },
  {
    value: "auditors",
    label: "For External Auditors",
    icon: IconClipboardCheck,
    color: "#be4bdb",
    features: [
      "Dedicated auditor portal with secure access",
      "Time-based facility access grants",
      "Audit session management and reporting",
      "Findings documentation with severity levels",
      "Multi-facility audit sessions",
      "Staffing analysis and compliance tracking",
      "Care notes and vital signs compliance reviews",
      "Attachment uploads for evidence",
    ],
  },
];

export function UseCasesSection() {
  const computedColorScheme = useComputedColorScheme("light");

  return (
    <Box
      component="section"
      id="use-cases"
      py={100}
      bg={computedColorScheme === "dark" ? "#25262B" : "#f8f9fa"}
    >
      <Container size="xl">
        <AnimateIn>
          <Stack align="center" gap="md" mb={60}>
            <Title order={2} size="2.5rem" fw={700} ta="center">
              Built for{" "}
              <Text
                component="span"
                inherit
                style={{
                  background:
                    "linear-gradient(135deg, #4a8c71 0%, #356651 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Every Role
              </Text>
            </Title>
            <Text size="lg" c="dimmed" ta="center" maw={700}>
              Tailored features for each member of your care team
            </Text>
          </Stack>
        </AnimateIn>

        <AnimateIn delay={0.1}>
          <Tabs
            defaultValue="managers"
            variant="pills"
            radius="md"
            // Active/hover styling lives in index.css — see the note there.
            className="usecase-tabs"
            styles={{
              root: { maxWidth: "900px", margin: "0 auto" },
              list: {
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: "0.5rem",
                marginBottom: "2rem",
              },
              tab: {
                fontSize: "0.95rem",
                fontWeight: 600,
                padding: "0.75rem 1.5rem",
                transition: "all 0.2s",
              },
            }}
          >
            <Tabs.List>
              {useCases.map((useCase) => {
                const Icon = useCase.icon;
                return (
                  <Tabs.Tab
                    key={useCase.value}
                    value={useCase.value}
                    leftSection={<Icon size={18} />}
                  >
                    {useCase.label}
                  </Tabs.Tab>
                );
              })}
            </Tabs.List>

            {useCases.map((useCase) => (
              <Tabs.Panel key={useCase.value} value={useCase.value}>
                <Box
                  p="xl"
                  style={{
                    borderRadius: "12px",
                    background:
                      computedColorScheme === "dark" ? "#1A1B1E" : "#ffffff",
                    border:
                      computedColorScheme === "dark"
                        ? "1px solid #373A40"
                        : "1px solid #e9ecef",
                  }}
                >
                  <List
                    spacing="md"
                    size="md"
                    icon={
                      <ThemeIcon
                        size={24}
                        radius="xl"
                        style={{ background: useCase.color }}
                      >
                        <IconCheck size={16} stroke={3} />
                      </ThemeIcon>
                    }
                  >
                    {useCase.features.map((feature, index) => (
                      <List.Item key={index}>
                        <Text size="sm" style={{ lineHeight: 1.6 }}>
                          {feature}
                        </Text>
                      </List.Item>
                    ))}
                  </List>
                </Box>
              </Tabs.Panel>
            ))}
          </Tabs>
        </AnimateIn>
      </Container>
    </Box>
  );
}
